import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
// Reveal/reference styles (apps-ref-*, apps-reveal-*, apps-eye-*) live here so
// they travel with the component on both the admin and the user detail pages.
import './apps.css';

// Revealed values auto-hide after this window, then are dropped from state.
export const REVEAL_TTL_MS = 20000;

export const REVEAL_ERROR_TEXT = {
  ssm_not_configured: 'SSM not configured',
  param_not_found: 'Value not found',
  ssm_error: 'SSM error — try again',
  ssm_access_denied: 'Access denied — check IAM permissions.',
  not_an_ssm_secret: 'Only secret references can be revealed.',
  forbidden: 'Not allowed to reveal this value.',
};

// One assigned reference. When `canReveal` is true an eye toggle reveals the
// decrypted value on demand: fetched live, shown transiently (auto-hides after
// REVEAL_TTL_MS), blurred when the window/tab loses focus, watermarked with the
// viewer identity + timestamp, and dropped from state the moment it is hidden.
// Screenshot prevention is best-effort only — deterrents, not real protection.
// When `canReveal` is false the value stays masked (no reveal button).
// Generic display label used when the SSM parameter NAME must not be shown to
// the viewer (non-admin users). The name is internal infrastructure detail; the
// value reveal still works (keyed on secret id server-side). A number is only
// appended when there's more than one in the group (datasets) — a lone secret
// reads as just "Secret", never "Secret 1".
function genericRefLabel(secret, index, total) {
  const base = secret.assignment_kind === 'warehouse' ? 'Data connection' : 'Secret';
  if (typeof index === 'number' && typeof total === 'number' && total > 1) {
    return `${base} ${index + 1}`;
  }
  return base;
}

export default function SecretRevealRow({
  secret,
  appId,
  viewerLabel,
  canReveal = true,
  canRemove = false,
  onRemove,
  hideName = false,   // when true, show a generic label instead of the SSM param name
  index,              // position within its group
  total,              // group size — generic labels only number when total > 1
}) {
  const [value, setValue] = useState(null);       // revealed value, or null when masked
  const [revealedAt, setRevealedAt] = useState(null); // timestamp shown in watermark
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [obscured, setObscured] = useState(false); // window blurred / tab hidden

  const mountedRef = useRef(true);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setValue(null);       // never keep the value in state beyond the visible window
    setRevealedAt(null);
    setObscured(false);
  }, [clearTimer]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearTimer(); };
  }, [clearTimer]);

  // Blur the revealed value when the window loses focus or the tab is hidden.
  useEffect(() => {
    if (value == null) return undefined;
    const onBlur = () => { if (mountedRef.current) setObscured(true); };
    const onFocus = () => { if (mountedRef.current) setObscured(false); };
    const onVis = () => { if (mountedRef.current) setObscured(document.visibilityState === 'hidden'); };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [value]);

  const reveal = useCallback(async () => {
    setErr(null);
    setBusy(true);
    try {
      // Always re-fetch — the value is never cached.
      const res = await axios.post(
        `${API_BASE_URL}/api/app_requests/${appId}/secrets/${secret.id}/reveal`,
        {},
        { withCredentials: true }
      );
      if (!mountedRef.current) return;
      setValue(res.data.value);
      setRevealedAt(new Date());
      setObscured(false);
      clearTimer();
      timerRef.current = setTimeout(() => { if (mountedRef.current) hide(); }, REVEAL_TTL_MS);
    } catch (e) {
      if (!mountedRef.current) return;
      const code = e?.response?.data?.error;
      setErr(REVEAL_ERROR_TEXT[code] || 'Couldn’t reveal');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [appId, secret.id, clearTimer, hide]);

  const isRevealed = value != null;
  // Admin sees the real SSM param name in a code chip. Users (hideName) get a
  // generic label — but when it's numbered ("Data connection 2") we still show
  // it; for a single unnumbered ref the group header already names it, so we
  // show a quiet "Reveal value" affordance instead of a redundant chip.
  const displayName = hideName ? genericRefLabel(secret, index, total) : secret.ssm_param_name;
  const numbered = typeof total === 'number' && total > 1;
  const showChip = !hideName || numbered;

  return (
    <li className="apps-ref-row">
      <div className="apps-ref-head">
        {showChip ? (
          <code className="apps-ref-chip" title={hideName ? undefined : secret.ssm_param_name}>{displayName}</code>
        ) : (
          <span className="apps-ref-reveal-label">Reveal value</span>
        )}
        <div className="apps-ref-actions">
          {canReveal && (
            <button
              type="button"
              className="apps-eye-btn"
              aria-label="Reveal value"
              title="Reveal value"
              disabled={busy}
              onClick={reveal}
            >
              {busy ? <Spinner /> : <EyeIcon />}
            </button>
          )}
          {canRemove && (
            <button type="button" className="btn btn-small btn-danger-outline" onClick={() => onRemove(secret.id)}>
              Remove
            </button>
          )}
        </div>
      </div>

      {err && <span className="apps-reveal-err" role="status">{err}</span>}

      {isRevealed && (
        <SecretRevealModal
          name={displayName}
          value={value}
          viewerLabel={viewerLabel}
          revealedAt={revealedAt}
          obscured={obscured}
          onClose={hide}
        />
      )}
    </li>
  );
}

// Pretty-print a revealed value: if it parses as a JSON object/array, return
// indented JSON; otherwise return the raw string. Scalars (a bare number/quoted
// string the SSM value happens to be) stay as their plain text — we only expand
// structured payloads so connection strings / config blobs are readable.
function formatRevealedValue(raw) {
  if (typeof raw !== 'string') return { text: String(raw ?? ''), isJson: false };
  const trimmed = raw.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return { text: JSON.stringify(parsed, null, 2), isJson: true };
      }
    } catch { /* not JSON — fall through to raw */ }
  }
  return { text: raw, isJson: false };
}

// Centered modal that shows a revealed value. Same deterrents as before
// (watermark, blur-on-blur, user-select:none) plus Escape/backdrop to close.
// The parent owns the value + auto-hide timer; closing here calls onClose,
// which drops the value from state. JSON payloads are pretty-printed; single
// values render inline. A copy button is offered for convenience.
function SecretRevealModal({ name, value, viewerLabel, revealedAt, obscured, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { text, isJson } = formatRevealedValue(value);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — no-op */ }
  }, [value]);

  const watermarkText = `${viewerLabel || 'viewer'} · ${revealedAt ? revealedAt.toLocaleString() : ''}`;

  return (
    <div className="apps-reveal-overlay" role="dialog" aria-modal="true" aria-label="Revealed secret value" onMouseDown={onClose}>
      <div className="apps-reveal-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="apps-reveal-modal-head">
          <code className="apps-reveal-modal-name" title={name}>{name}</code>
          {isJson && <span className="apps-reveal-kind" aria-hidden="true">JSON</span>}
          <button type="button" className="apps-reveal-copy" onClick={copy} aria-label="Copy value">
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="apps-reveal-modal-close" aria-label="Hide value" onClick={onClose}>×</button>
        </div>

        <div className={`apps-reveal-value-wrap${obscured ? ' is-obscured' : ''}`}>
          {isJson
            ? <pre className="apps-reveal-value apps-reveal-value--json" aria-live="polite">{text}</pre>
            : <span className="apps-reveal-value" aria-live="polite">{text}</span>}
          <span className="apps-reveal-watermark" aria-hidden="true">
            {watermarkText} {watermarkText} {watermarkText} {watermarkText}
          </span>
          {obscured && (
            <span className="apps-reveal-obscure" aria-hidden="true">Hidden — refocus to view</span>
          )}
        </div>

        <p className="apps-reveal-modal-note">
          Shown transiently — auto-hides shortly, never stored. This reveal is audit-logged.
        </p>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" className="apps-eye-spin">
      <path d="M21 12a9 9 0 1 1-6.2-8.5" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
